import { useState, useEffect, useRef } from 'react';
import { validateEmail } from './validation';

/**
 * Forgot password modal — otvara se iz Password taba.
 * Naslov: Reset lozinke, Opis: Unesite email i poslaćemo vam link za reset.
 * Backend: /auth/forgot-password (stub ako ne postoji).
 */
export default function ForgotPasswordModal({ isOpen, onClose, requestPasswordReset }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError('');
      setFieldError('');
      setSuccess(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldError('');

    const res = validateEmail(email);
    if (!res.valid) {
      setFieldError(res.message);
      return;
    }

    setIsLoading(true);
    try {
      const result = await requestPasswordReset(email);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Greška pri slanju linka za reset.');
      }
    } catch (err) {
      setError(err.message || 'Nepoznata greška.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="forgot-password-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <h2 id="forgot-password-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Reset lozinke
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Unesite email i poslaćemo vam link za reset.
        </p>

        {success ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-sm text-green-800 dark:text-green-200 space-y-2">
              <p>Link za reset je poslat na <strong>{email}</strong>.</p>
              <p>Proverite Inbox i <strong>Spam</strong> folder. Ako email ne stigne u 5 minuta, ponovo zatražite link ili nas kontaktirajte na podrška (npr. putem stranice Kontakt na sajtu).</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary w-full"
            >
              Zatvori
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">
                Email adresa
              </label>
              <input
                ref={inputRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFieldError('');
                  setError('');
                }}
                required
                autoComplete="email"
                className={`input-base ${fieldError ? 'input-error' : ''}`}
                placeholder="vas@email.com"
              />
              {fieldError && <p className="error-text">{fieldError}</p>}
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex-1"
              >
                Nazad
              </button>
              <button
                type="submit"
                disabled={isLoading || !email}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Šalje se...
                  </>
                ) : (
                  'Pošalji link za reset'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
